import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Extension",
  "Renovation",
  "Refurbishment",
  "Conversion",
  "New Build",
  "Project Management",
  "Other",
];

const BUDGETS = [
  "Under £25,000",
  "£25,000 – £50,000",
  "£50,000 – £100,000",
  "£100,000 – £250,000",
  "£250,000+",
  "Prefer not to say",
];

const inputCls =
  "w-full border border-[#C5A059]/20 bg-[#0E1726]/70 px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-[#C5A059]";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  property_address: "",
  project_type: "",
  budget: "",
  description: "",
  start_date: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email address.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. We'll be in touch to discuss your project.");
      setForm(initialForm);
    } catch {
      toast.error("Something went wrong sending your enquiry. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#050B14] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
        <Reveal>
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-gradient-to-r from-[#C5A059] to-transparent" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#C5A059]">Contact</span>
          </div>
          <h2 className="font-serif text-3xl font-light uppercase leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
            Start the <span className="gold-text-gradient">Conversation.</span>
          </h2>
          <p className="mt-7 text-base font-light leading-relaxed text-slate-400">
            Tell us about your property and what you're looking to achieve. Every enquiry
            is reviewed personally and we'll respond to plan the next step.
          </p>

          <div className="mt-10 space-y-5">
            <div className="font-serif text-2xl uppercase tracking-[0.12em] text-white">Tyrell Property Services</div>
            <a href="tel:+447555297690" data-testid="contact-phone-link" className="flex items-center gap-4 text-slate-300 transition-colors hover:text-[#C5A059]">
              <span className="flex h-11 w-11 items-center justify-center border border-[#C5A059]/30"><Phone size={16} className="text-[#C5A059]" /></span>
              07555 297690
            </a>
            <a href="mailto:Tyrellservicesltd@gmail.com" data-testid="contact-email-link" className="flex items-center gap-4 text-slate-300 transition-colors hover:text-[#C5A059]">
              <span className="flex h-11 w-11 items-center justify-center border border-[#C5A059]/30"><Mail size={16} className="text-[#C5A059]" /></span>
              Tyrellservicesltd@gmail.com
            </a>
            <div className="flex items-center gap-4 text-slate-300" data-testid="contact-area">
              <span className="flex h-11 w-11 items-center justify-center border border-[#C5A059]/30"><MapPin size={16} className="text-[#C5A059]" /></span>
              Essex &amp; surrounding areas
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="enquiry-form" className="border border-[#C5A059]/20 bg-[#0A111E]/60 p-7 sm:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="enq-name" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Name *</label>
                <input id="enq-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="enq-email" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Email *</label>
                <input id="enq-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
              </div>
              <div>
                <label htmlFor="enq-phone" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Phone</label>
                <input id="enq-phone" data-testid="enquiry-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="07000 000000" />
              </div>
              <div>
                <label htmlFor="enq-address" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Property Address</label>
                <input id="enq-address" data-testid="enquiry-address-input" value={form.property_address} onChange={set("property_address")} className={inputCls} placeholder="Street, Town, Postcode" />
              </div>
              <div>
                <label htmlFor="enq-type" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Project Type</label>
                <select id="enq-type" data-testid="enquiry-type-select" value={form.project_type} onChange={set("project_type")} className={inputCls}>
                  <option value="">Select a project type</option>
                  {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="enq-budget" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Estimated Budget</label>
                <select id="enq-budget" data-testid="enquiry-budget-select" value={form.budget} onChange={set("budget")} className={inputCls}>
                  <option value="">Select a budget range</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enq-date" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Preferred Start Date</label>
                <input id="enq-date" data-testid="enquiry-date-input" type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="enq-description" className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">Project Description</label>
                <textarea id="enq-description" data-testid="enquiry-description-input" rows={5} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} placeholder="Tell us about your property and what you'd like to achieve…" />
              </div>
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit-button"
              disabled={sending}
              className="group mt-8 flex w-full items-center justify-center gap-3 bg-[#C5A059] px-8 py-4 text-xs font-bold uppercase tracking-[0.3em] text-[#050B14] transition-all duration-300 hover:bg-[#E6CA65] disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
